import React from 'react'
import { List, useTable, ShowButton, DateField } from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space } from 'antd'
import { brand } from '../../theme/brand'
import { useRowShow, ListPage, Pill, TableSurface, type Stat, type Tone } from '../../theme/listkit'
import { renderAvatarName } from '../../theme/columns'
import { GraduationCap, BadgeCheck, Clock, XCircle } from '../../components/icons'

export interface ClassYearChangeRequest {
  id: string
  user_id: string
  current_class_year: string | null
  requested_class_year: string
  reason: string | null
  status: 'pending' | 'approved' | 'rejected' | 'cancelled'
  decided_by: string | null
  decided_at: string | null
  decision_note: string | null
  created_at: string
  updated_at: string
  // FK embed — the requester's profile, so the queue never shows a bare uuid.
  // `id` (profiles.id, NOT user_id) is embedded only by the Show page, for its profile link.
  requester?: { id?: string; name: string | null; avatar_url: string | null; class_year: string | null } | null
}

/** Requester profile pulled inline via the profiles FK (no second round trip). */
const SELECT =
  '*, requester:profiles!class_year_change_requests_user_profile_fkey(name,avatar_url,class_year)'

export const statusTone = (status: string): Tone => {
  if (status === 'pending') return 'gold'
  if (status === 'approved') return 'green'
  if (status === 'rejected') return 'red'
  return 'neutral'
}

/**
 * Class Year Changes — the approval queue for member-requested class-year changes.
 *
 * A member's class year is permanent after onboarding (guard_profile_class_year),
 * so every change lands here. Approving is not just a status flip: the
 * class-year-request-decide Edge Function applies the profile change AND moves the
 * member's Stream class chat, so decisions are made on the Show page (which calls
 * that function), never by editing the row.
 *
 * Stat counts are four bounded pageSize:1 queries reading `result.total`, so
 * PostgREST returns counts without ever shipping the rows (same as the Reports queue).
 */
export function ClassYearChangeRequestsList() {
  const { tableProps } = useTable<ClassYearChangeRequest>({
    resource: 'class_year_change_requests',
    syncWithLocation: true,
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    meta: { select: SELECT },
  })
  const onRow = useRowShow('class_year_change_requests')

  // Four bounded pageSize:1 count queries, spelled out (not looped) so the hook
  // call order is statically obvious — same shape as the Reports queue.
  const pendingQ = useList<ClassYearChangeRequest>({
    resource: 'class_year_change_requests',
    filters: [{ field: 'status', operator: 'eq', value: 'pending' }],
    pagination: { pageSize: 1 },
  })
  const approvedQ = useList<ClassYearChangeRequest>({
    resource: 'class_year_change_requests',
    filters: [{ field: 'status', operator: 'eq', value: 'approved' }],
    pagination: { pageSize: 1 },
  })
  const rejectedQ = useList<ClassYearChangeRequest>({
    resource: 'class_year_change_requests',
    filters: [{ field: 'status', operator: 'eq', value: 'rejected' }],
    pagination: { pageSize: 1 },
  })
  const cancelledQ = useList<ClassYearChangeRequest>({
    resource: 'class_year_change_requests',
    filters: [{ field: 'status', operator: 'eq', value: 'cancelled' }],
    pagination: { pageSize: 1 },
  })

  const pending = pendingQ.result?.total ?? 0
  const approved = approvedQ.result?.total ?? 0
  const rejected = rejectedQ.result?.total ?? 0
  const cancelled = cancelledQ.result?.total ?? 0

  const stats: Stat[] = [
    {
      label: 'Awaiting review',
      value: pending.toLocaleString(),
      icon: Clock,
      tone: pending > 0 ? 'gold' : 'neutral',
    },
    { label: 'Approved', value: approved.toLocaleString(), icon: BadgeCheck, tone: approved > 0 ? 'green' : 'neutral' },
    { label: 'Not approved', value: rejected.toLocaleString(), icon: XCircle, tone: 'neutral' },
    { label: 'Withdrawn', value: cancelled.toLocaleString(), icon: XCircle, tone: 'neutral' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Members · Class Year Changes',
        description:
          "A member's class year is set once during onboarding and is permanent after that, because it drives their class chat, class standings, and the alumni directory. Requests to change it land here for review — open one to see the member and approve or decline it.",
      }}
      stats={stats}
      statColumns={4}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<ClassYearChangeRequest>
              dataIndex="user_id"
              title="Member"
              render={(_: unknown, record) => renderAvatarName(record.requester?.name ?? 'Unknown member')}
            />
            <Table.Column<ClassYearChangeRequest>
              dataIndex="requested_class_year"
              title="Change"
              render={(_: unknown, record) => (
                <Space size={6}>
                  <Pill tone="neutral">{record.current_class_year ?? '—'}</Pill>
                  <span style={{ color: brand.sub, fontWeight: 700 }}>→</span>
                  <Pill tone="jaguar">{record.requested_class_year}</Pill>
                </Space>
              )}
            />
            <Table.Column<ClassYearChangeRequest>
              dataIndex="reason"
              title="Reason"
              render={(reason: string | null) => (
                <span
                  style={{
                    fontFamily: brand.fSans,
                    fontWeight: 500,
                    fontSize: 12.5,
                    color: reason ? brand.ink : brand.sub,
                    fontStyle: reason ? 'normal' : 'italic',
                    display: 'inline-block',
                    maxWidth: 260,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={reason ?? undefined}
                >
                  {reason || 'No reason given'}
                </span>
              )}
            />
            <Table.Column<ClassYearChangeRequest>
              dataIndex="status"
              title="Status"
              render={(status: string) => <Pill tone={statusTone(status)}>{status}</Pill>}
            />
            <Table.Column<ClassYearChangeRequest>
              dataIndex="created_at"
              title="Requested"
              sorter
              render={(value: string) => <DateField value={value} format="MMM D, YYYY" />}
            />
            <Table.Column<ClassYearChangeRequest>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
